import * as React from 'react';
import { FormLabel, FormGroup, FormHelperText, FormControl, Collapse, makeStyles } from '@material-ui/core';
import { OtherTextField } from 'components/common/OtherTextField/OtherTextField';
import CheckBox from '../Checkbox/Checkbox';
import { uuid } from '../../../utils/functions';

const useStyles = makeStyles(() => {
  return {
    checkboxGroupContainer: {
      width: '90%',
      '& .threepp-checkbox-wrapper': {
        width: 'max-content',
        maxWidth: '100%',
        display: 'inline-flex',
        alignItems: 'start'
      }
    }
  };
});

export const CheckboxGroup = props => {
  const { checkboxGroupContainer } = useStyles();
  const {
    t,
    options,
    disabled,
    checked: initialCheckedValues,
    onChange,
    inputValidation,
    onValidationError,
    error: initialError,
    otherText: initialOtherText,
    intermediate,
    row,
    title,
    otherTextMaxCharacters
  } = props;

  // State Management
  const [checkedValues, setCheckedValues] = React.useState(initialCheckedValues || []);
  const [error, setError] = React.useState(initialError);
  const [otherText, setOtherText] = React.useState(initialOtherText || '');
  const [otherTextError, setOtherTextError] = React.useState(!initialOtherText);

  // Local Vars
  const possibleValueWithOtherText =
    options.find(possibleValue => {
      return possibleValue.enableOtherText;
    }) || {};
  const showOtherText = checkedValues.indexOf(possibleValueWithOtherText.value) > -1;

  // Side Effects
  React.useEffect(() => {
    setCheckedValues(initialCheckedValues || []);
  }, [initialCheckedValues]);

  React.useEffect(() => {
    setOtherText(initialOtherText);
    setOtherTextError(!initialOtherText);
  }, [initialOtherText]);

  // Update error if error prop changes
  React.useEffect(() => {
    setError(initialError);
  }, [initialError]);

  // Event Handlers
  const handleValueChange = React.useCallback(
    e => {
      const { checked, value } = e.target;
      const updatedValues = [...checkedValues];
      const isNotNumber = isNaN(value);
      if (checked) {
        isNotNumber ? updatedValues.push(value) : updatedValues.push(Number(value));
      } else {
        const index = isNotNumber ? updatedValues.indexOf(value) : updatedValues.indexOf(Number(value));
        updatedValues.splice(index, 1);
      }
      setCheckedValues(updatedValues);

      // Other Text and Min/Max Error handling
      const otherTextIsRequired = possibleValueWithOtherText.value && updatedValues.indexOf(possibleValueWithOtherText.value) > -1;
      const otherTextIsMissing = otherTextError && otherTextIsRequired;

      let errorObj = null;
      const numChecked = updatedValues.length;
      errorObj = inputValidation && numChecked > 0 && inputValidation(numChecked);
      if (errorObj || otherTextIsMissing) {
        onValidationError(updatedValues, otherTextIsRequired ? otherText : null, errorObj);
      } else {
        onChange(updatedValues, otherTextIsRequired ? otherText : null);
      }
      const newError = errorObj ? t(errorObj.key, { ...errorObj.args }) : null;
      setError(newError);
    },
    [checkedValues, inputValidation, onChange, onValidationError, otherText, otherTextError, possibleValueWithOtherText.value, t]
  );
  const handleOtherTextChange = React.useCallback(
    e => {
      const { value } = e.target;
      setOtherTextError(!value);
      setOtherText(value);
      if (!value) {
        onValidationError(checkedValues, value);
      } else {
        onChange(checkedValues, value);
      }
    },
    [checkedValues, onChange, onValidationError]
  );

  // Render
  const checkboxes = options.map(option => {
    const { value, enableOtherText, defaultOtherText } = option;
    const isChecked = checkedValues.includes(value);
    const isIntermediate = intermediate.includes(value);
    return (
      <React.Fragment key={option.value}>
        <CheckBox
          testId={option.testId || uuid()}
          checked={isChecked}
          disabled={disabled}
          indeterminate={isChecked ? false : isIntermediate}
          value={option.value}
          onChange={handleValueChange}
          label={option.label}
          color={option.color}
        />
        {enableOtherText && (
          <Collapse in={showOtherText}>
            <OtherTextField
              value={otherText}
              onChange={handleOtherTextChange}
              label={defaultOtherText}
              required
              error={otherTextError}
              disabled={disabled}
              maxCharactersAllowed={otherTextMaxCharacters}
            />
          </Collapse>
        )}
      </React.Fragment>
    );
  });

  return (
    <>
      <FormControl className={checkboxGroupContainer}>
        {title && <FormLabel>{title}</FormLabel>}
        <FormGroup row={row}>{checkboxes}</FormGroup>
      </FormControl>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </>
  );
};

CheckboxGroup.defaultProps = {
  t: k => k,
  options: [],
  checked: [],
  intermediate: [],
  inputValidation: () => null,
  onValidationError: e => e
};
